import { DOSAGE_FORMS, WEIGHT_DOSED_FORMS } from "@shafa/shared";

// Shared by the medicine SIG form and the Generic SIG settings editor, so a SIG
// composed in either place reads the same.

// Dose unit word that follows "take" in the SIG ("1 tablet ...", "১ চামচ ...").
export function getSigTypeWord(type, language) {
    if (type?.includes('Tablet') || type?.includes('tablet')) {
        return language == 'bangla' ? 'টি ট্যাবলেট' : 'tablet'
    } else if (type?.includes('Capsule') || type?.includes('capsule')) {
        return language == 'bangla' ? 'টি ক্যাপসুল' : 'Capsule'
    } else if (type?.includes('Drop') || type?.includes('drop')) {
        return language == 'bangla' ? 'ফোটা' : 'Drop'
    } else if (type?.includes('Syrup') || type?.includes('syrup')) {
        return language == 'bangla' ? 'চামচ' : 'spoon'
    } else if (type?.includes('Suspension') || type?.includes('Solution')
        || type?.includes('Powder') || type?.includes('suspension') || type?.includes('solution')
        || type?.includes('powder')) {
        return ""
    } else if (type?.includes('Injection') || type?.includes('Infusion') || type?.includes('injection') || type?.includes('infusion')) {
        return language == 'bangla' ? 'ইনজেকশন' : 'injection'
    } else {
        return ""
    }
}

export function composeSig({ take, typeWord, route, frequency, instruction }) {
    return (take ? `${take} ${typeWord}` : "")
        + (route ? " " + `${route}` : "")
        + (frequency ? " " + `(${frequency})` : "")
        + " " + (instruction || "")
}

// Stored as "<count>/<unit>" ("2/Day(s)"), or a bare "continue" / "চলবে".
export function parseDuration(duration) {
    return {
        duration: duration?.split("/")[0] || "",
        duration_time: duration?.split("/")[1],
    }
}

export function buildDuration(info) {
    if (info?.duration == "continue" || info?.duration == "চলবে") return info?.duration
    return info?.duration + (info?.duration_time ? "/" + info?.duration_time : "")
}

// Master medicine type -> one of `dosageForms` ("Tablet (Film Coated)" -> "Tablet"), or "".
export function findDosageForm(type) {
    const value = (type || "").toLowerCase()
    if (!value) return ""
    return DOSAGE_FORMS.find((form) => value.includes(form.toLowerCase())) || ""
}

// Same as the API's genericKey: trimmed, single-spaced, lower-case.
export function normGeneric(generic) {
    return String(generic || "").trim().replace(/\s+/g, " ").toLowerCase()
}

// "500 MG" / "500mg" -> "500mg"
export function normPower(power) {
    return String(power || "").toLowerCase().replace(/\s+/g, "")
}

// 0 = preset made for this medicine's form, 1 = preset for any form, 2 = another form.
export function genericSigFormRank(preset, medicineType) {
    if (!preset?.form) return 1
    const form = findDosageForm(medicineType)
    return form && preset.form === form ? 0 : 2
}

// 0 = same strength, 1 = preset for any strength, 2 = another strength.
export function genericSigPowerRank(preset, medicinePower) {
    if (!preset?.power) return 1
    return normPower(preset.power) === normPower(medicinePower) ? 0 : 2
}

// Best preset first: matching form, matching strength, same language, most used.
export function rankGenericSigs(presets = [], { medicineType, medicinePower, language } = {}) {
    const lang = language || "english"
    return [...presets].sort((a, b) =>
        genericSigFormRank(a, medicineType) - genericSigFormRank(b, medicineType)
        || genericSigPowerRank(a, medicinePower) - genericSigPowerRank(b, medicinePower)
        || ((a.language || "english") === lang ? 0 : 1) - ((b.language || "english") === lang ? 0 : 1)
        || (b.usageCount || 0) - (a.usageCount || 0)
    )
}

// Weight/age-dosed forms (syrups, drops, ...): offer saved SIGs, never auto-fill.
export function isWeightDosed(medicineType) {
    return WEIGHT_DOSED_FORMS.includes(findDosageForm(medicineType))
}

// A preset may fill the form / add the medicine without the doctor choosing it only
// when it can't be the wrong dose: same form, strength empty-or-equal, same language.
export function canAutoFillGenericSig(preset, medicine, language) {
    if (!preset?.sig || isWeightDosed(medicine?.type)) return false
    if (preset.form && preset.form !== findDosageForm(medicine?.type)) return false
    if (genericSigPowerRank(preset, medicine?.power) === 2) return false
    return (preset.language || "english") === (language || "english")
}

// Presets for a medicine, best first, plus the one safe to auto-fill (or null).
export function getGenericSigSuggestion(presets, medicine, language) {
    const ranked = rankGenericSigs(presets, { medicineType: medicine?.type, medicinePower: medicine?.power, language })
    const autoFill = ranked.find((preset) => canAutoFillGenericSig(preset, medicine, language)) || null
    return { presets: ranked, autoFill }
}

// Form fields a preset fills in the SIG form.
export function genericSigToInfo(preset) {
    return {
        take: preset?.take || "",
        route: preset?.route || "",
        frequency: preset?.frequency || "",
        instruction: preset?.instruction || "",
        ...parseDuration(preset?.duration),
    }
}

// "2/Day(s)" -> "2 Day(s)" for display.
export function formatDuration(duration) {
    return (duration || "").replace("/", " ")
}
